/**
 * useGedcomUpload Hook
 *
 * Manages GEDCOM file upload workflow:
 * 1. Validate file extension (.ged, .gedcom)
 * 2. Validate file size (max 10MB)
 * 3. Detect character encoding locally
 * 4. Upload file to Supabase Storage (gedcom-temp bucket)
 *
 * Returns upload state and metadata for next processing steps.
 */

import { useState } from 'react';
import { supabase } from '@/config/supabase';
import { detectEncoding, type EncodingDetectionResult } from '../utils/encodingDetection';

// Upload state machine
export type UploadState = 'idle' | 'detecting' | 'uploading' | 'success' | 'error';

// Uploaded file metadata
export interface UploadedFileMetadata {
  storagePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  detectedEncoding: EncodingDetectionResult;
  uploadedAt: string;
}

// Error types
export interface UploadError {
  code: 'INVALID_EXTENSION' | 'FILE_TOO_LARGE' | 'UPLOAD_FAILED' | 'UNKNOWN';
  message: string;
}

// Hook interface
interface UseGedcomUploadReturn {
  uploadState: UploadState;
  uploadFile: (file: File) => Promise<UploadedFileMetadata>;
  error: UploadError | null;
  metadata: UploadedFileMetadata | null;
  reset: () => void;
}

// Constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.ged', '.gedcom'];
const STORAGE_BUCKET = 'gedcom-temp';

/**
 * Custom hook for GEDCOM file upload
 */
export const useGedcomUpload = (): UseGedcomUploadReturn => {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [error, setError] = useState<UploadError | null>(null);
  const [metadata, setMetadata] = useState<UploadedFileMetadata | null>(null);

  /**
   * Upload GEDCOM file
   */
  const uploadFile = async (file: File): Promise<UploadedFileMetadata> => {
    try {
      // Reset state
      setError(null);
      setMetadata(null);

      // Step 1: Validate file extension
      const extension = getFileExtension(file.name);
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        const err: UploadError = {
          code: 'INVALID_EXTENSION',
          message: `Invalid file type. Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed.`,
        };
        setError(err);
        setUploadState('error');
        throw err;
      }

      // Step 2: Validate file size
      if (file.size > MAX_FILE_SIZE) {
        const err: UploadError = {
          code: 'FILE_TOO_LARGE',
          message: `File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`,
        };
        setError(err);
        setUploadState('error');
        throw err;
      }

      // Step 3: Detect encoding
      setUploadState('detecting');
      const buffer = await file.arrayBuffer();
      const encodingResult = detectEncoding(buffer);

      // Step 4: Upload to Supabase Storage
      setUploadState('uploading');

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        const err: UploadError = {
          code: 'UPLOAD_FAILED',
          message: 'User not authenticated',
        };
        setError(err);
        setUploadState('error');
        throw err;
      }

      // Generate unique file path: {userId}/{timestamp}-{sanitizedFileName}
      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFileName(file.name);
      const storagePath = `${user.id}/${timestamp}-${sanitizedFileName}`;

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError || !data) {
        const err: UploadError = {
          code: 'UPLOAD_FAILED',
          message: uploadError?.message || 'Failed to upload file to storage',
        };
        setError(err);
        setUploadState('error');
        throw err;
      }

      // Step 5: Build metadata
      const uploadMetadata: UploadedFileMetadata = {
        storagePath: data.path,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/x-gedcom',
        detectedEncoding: encodingResult,
        uploadedAt: new Date().toISOString(),
      };

      setMetadata(uploadMetadata);
      setUploadState('success');

      return uploadMetadata;
    } catch (err) {
      // Catch any unexpected errors
      if (error === null) {
        const unknownErr: UploadError = {
          code: 'UNKNOWN',
          message: err instanceof Error ? err.message : 'An unknown error occurred',
        };
        setError(unknownErr);
        setUploadState('error');
      }
      throw err;
    }
  };

  /**
   * Reset hook state
   */
  const reset = () => {
    setUploadState('idle');
    setError(null);
    setMetadata(null);
  };

  return {
    uploadState,
    uploadFile,
    error,
    metadata,
    reset,
  };
};

/**
 * Get file extension (lowercase, with dot)
 */
const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return '';
  return fileName.substring(lastDot).toLowerCase();
};

/**
 * Sanitize filename for storage (remove special characters, preserve extension)
 */
const sanitizeFileName = (fileName: string): string => {
  // Split name and extension
  const lastDot = fileName.lastIndexOf('.');
  const name = lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
  const ext = lastDot > 0 ? fileName.substring(lastDot) : '';

  // Sanitize name: alphanumeric, hyphens, underscores only
  const sanitized = name.replace(/[^a-zA-Z0-9_-]/g, '_');

  return `${sanitized}${ext}`;
};
